import { defineStore } from 'pinia';
import { deepDiffObj } from '../utils/deepDiff';
import { transformVO } from '../utils/transformVO';
import { useI18n } from '../utils/useI18n';
import { useInstance } from 'common-module/src/util/useInstance';
import { useVuex } from '../utils/useVuex';
import { computed, readonly, toRaw, watch, ref } from '@vue/composition-api';
import { createEventHook } from '@vueuse/core';
import { validateForm } from '@zk/framework-vue';

import { IssueRelationship } from '../models/issue';
import { IssueDrawerActionCreate, useIssueAction } from './action';
import { useComment } from './comment';
import { useFields } from './fields';
import { useFormula } from './formula';
import { useIssue } from './issue';
import { useIssueType } from './issueType';
import { useLayout } from './layout';
import { useProject } from './project';
import { useChangeRecord } from './record';
import { useRelationNavigation } from './relationNavigation';
import { useShare } from './share';
import { useSprint } from './sprint';
import { useThirdTools } from './thirdTools';
import { useTurnPage } from './turnPage';
import { useWorkflow } from './workflow';
import { useReport } from './report';
import { useUIState } from './ui';
import { useRecorder } from './recorder';
import { usePermission } from './permission';
import { useIdb } from './idb';
import { useLeftNavigationLabel } from './leftNavigationLabel';

export const useIssueDetail = defineStore('issueDetail', () => {
  const vuex = useVuex();
  const instance = useInstance();

  const opened = ref(false);
  const { localDb } = useIdb();
  const issueLoadCompleteEvent = createEventHook<void>();
  const shareStore = useShare();
  const actionStore = useIssueAction(localDb);
  const issueStore = useIssue(actionStore, shareStore);
  const projectStore = useProject(
    actionStore,
    issueStore.issueAssemble,
    shareStore
  );

  const permissionStore = usePermission(projectStore);

  const sprintStore = useSprint(
    actionStore,
    issueStore.issueAssemble,
    () => fieldStore
  );

  const issueTypeStore = useIssueType(
    actionStore,
    issueStore.issueAssemble,
    projectStore,
    sprintStore,
    () => fieldStore
  );

  const workflowStore = useWorkflow(actionStore, issueTypeStore);

  const layoutStore = useLayout(
    actionStore,
    projectStore,
    issueTypeStore,
    issueStore.issueAssemble,
    () =>
      projectStore.currentProjectId.value &&
      issueTypeStore.currentTypeId.value &&
      fieldStore.refreshOptions({
        projectId: projectStore.currentProjectId.value,
        issueTypeId: issueTypeStore.currentTypeId.value
      }),
    shareStore,
    permissionStore
  );

  const fieldStore = useFields(
    actionStore,
    projectStore,
    issueTypeStore,
    layoutStore,
    issueStore,
    shareStore
  );


  const recordStore = useChangeRecord(
    actionStore,
    fieldStore,
    issueStore,
    shareStore.isVisitor
  );

  const leftNavigationLabelStore = useLeftNavigationLabel(
    actionStore,
    layoutStore.allRequiredFieldCodes,
    layoutStore.leftAreaFieldCodes,
    issueStore.issueAssemble,
    () => fieldStore,
    projectStore
  );

  const recorderStore = useRecorder();

  const show = computed(() => {
    const action = actionStore.action.value;
    if (actionStore.focusDrawer.value || !action) {
      return actionStore.focusDrawer.value;
    }
    return action.type !== 'Edit' || issueStore.isLoadComplete.value ||
      actionStore.isIssueDeleted.value;
  });

  const disabled = computed(() => shareStore.isVisitor.value);

  /**
   * 当前状态下的字段可编辑配置
   * 根据 stateEditableVOList 和当前 status 值，返回当前状态对应的 layoutFieldEditable
   * 如果未配置或找不到匹配的状态，返回 null（表示所有字段保持默认可编辑）
   */
  const stateFieldEditable = computed<Record<string, number> | null>(() => {
    const stateEditableList = issueStore.issueAssemble.value?.stateEditableVOList;
    if (!stateEditableList || stateEditableList.length === 0) {
      return null;
    }
    const currentStatus = fieldStore.fieldValues.value?.status;
    if (!currentStatus) {
      return null;
    }
    const matched = stateEditableList.find(
      item => item.workflowStateId === currentStatus
    );
    return matched?.layoutFieldEditable ?? null;
  });

  const loading = computed(() => {
    if (actionStore.action.value) {
      if (
        actionStore.action.value.type === 'Create' ||
        actionStore.action.value.type === 'PreviewType'
      ) {
        return (
          !(
            issueStore.issueAssemble.value &&
            layoutStore.fieldAreas.value.length
          ) || layoutStore.isLoadingLayout.value
        );
        // eslint-disable-next-line no-else-return
      } else if (actionStore.action.value.type === 'Edit') {
        return (
          !(
            issueStore.issueAssemble.value &&
            projectStore.projectList.value &&
            sprintStore.sprints.value &&
            fieldStore.fieldValues.value?.id
          ) || layoutStore.isLoadingLayout.value
        );
      }
    }

    if (actionStore.isIssueDeleted.value) {
      return false;
    }

    return true;
  });

  const hasChanged = (
    values: Record<string, any>,
    defaultValue: Record<string, any>
  ) => {
    if (values) {
      const keys = Object.keys(
        deepDiffObj(toRaw(defaultValue), toRaw(values))
      ).filter(
        k =>
          fieldStore.fieldCodeMap.value[k]?.controlCode !== 'formula' &&
              !k.endsWith('VO') &&
              !k.endsWith('Detail') &&
              ![
                'autoCalculateFlag',
                'status',
                'relationship',
                'attachment',
                'subtask',
                'recordVideoCodes',
                'dateRange',
                'docs',
                'labourRecordDetail'
              ].includes(k)
      );

      if (defaultValue.estimatePoint !== values.estimatePoint && !keys.includes('estimatePoint')) {
        keys.push('estimatePoint');
      }

      if (keys.includes('estimatePoint')) {
        if (
          defaultValue.autoCalculateFlag === 1 &&
            values.autoCalculateFlag === 1
        ) {
          keys.splice(keys.indexOf('estimatePoint'), 1);
        }
      }
      console.log(`fields [${keys.join(',')}] changed`);
      return keys.length > 0;
    }
    return false;
  };

  const waitChangedFlushOnEdit = () => {
    return new Promise<void>((resolve, reject) => {
      if (
        actionStore.action.value?.type === 'Edit' &&
        !shareStore.isVisitor.value
      ) {
        window.focus();
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement.blur) {
          activeElement.blur();
        }
        const inEditEditors = document.querySelectorAll(
          '.share-editor.in-edit'
        );
        inEditEditors.forEach((editor) => {
          (editor as any).__vue__?.onClickOutside?.();
        });
        setTimeout(async () => {
          if (
            actionStore.isChanged.value ||
            hasChanged(
              issueStore.issueAssemble.value?.issueVO.data || {},
              fieldStore.fieldValues.value
            )
          ) {
            const rootForm = (document.getElementById('issue-root-form') as any)
              ?.__vue__;
            if (rootForm) {
              try {
                await validateForm(rootForm);
                await fieldStore.saveChangedImmediately();
                if (
                  actionStore.action.value?.type === 'Edit' &&
                  actionStore.action.value.issueId
                ) {
                  triggerIssueSubmitEvent(actionStore.action.value.issueId);
                }
                resolve();
              } catch (e) {
                const { activeRightTab, changeRightTab } = layoutStore;
                if (activeRightTab.value !== 'field') {
                  changeRightTab('field');
                }
                reject(e);
              }
            }
          } else if (recordStore.isLabourRecordChanged.value) {
            if (
              actionStore.action.value?.type === 'Edit' &&
              actionStore.action.value.issueId
            ) {
              triggerIssueSubmitEvent(actionStore.action.value.issueId);
            }
            resolve();
          } else {
            resolve();
          }
        }, 0);
      } else {
        resolve();
      }
    });
  };

  const triggerIssueSubmitEvent = (id: number | null) => {
    if (id) {
      const eventData = transformVO(
        fieldStore.fieldValues.value,
        fieldStore.optionsMap.value,
        vuex.state.user.account.userId,
        fieldStore.fieldValues.value.subtask
      );
      instance.eventBus.$emit('issue:submit', id, false, toRaw(eventData));
    }
  };

  const stopRecorder = (options?: {
    addIssueId?: number;
    isCancel?: boolean;
  }) => {
    const isRecording = recorderStore.isIssueDescRecording.value;
    isRecording && instance.eventBus.$emit('recorder:stop', options);
    recorderStore.setRecordOptions({ issueId: undefined, from: '' });
  };

  const showEditCloseMessageBox = () => {
    return new Promise<void>((resolve, reject) => {
      const message = instance.$t('issue.dialog.recordCloseConfirm.editMsg');
      const title = instance.$t('issue.dialog.closeConfirm.title');
      instance.$msgbox({
        title,
        message,
        confirmButtonText: instance.$t(
          'issue.dialog.recordCloseConfirm.confirmBtn'
        ),
        cancelButtonText: instance.$t(
          'issue.dialog.recordCloseConfirm.cancelBtn'
        ),
        showCancelButton: true,
        closeOnPressEscape: false,
        distinguishCancelAndClose: true,
        callback: async (action: 'close' | 'confirm' | 'cancel') => {
          if (['cancel', 'confirm'].includes(action)) {
            stopRecorder({ isCancel: action === 'cancel' });
          }
          action === 'close' ? reject() : resolve();
        }
      });
      instance.$track.send(
        'Issue_RemindLeave',
        { Issue_Status: '编辑', Has_Record_Video: '是' },
        true
      );
    });
  };

  const safeClose = (forceCreate = false, isContinue = false) => {
    const isRecording = recorderStore.isIssueDescRecording.value;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<number | undefined>(async (resolve, reject) => {
      if (actionStore.action.value && !shareStore.isVisitor.value) {
        if (actionStore.action.value.type === 'Create') {
          if (forceCreate) {
            const res = await fieldStore.submitCreateIssue(isContinue);
            if (res) {
              if (!isContinue) {
                actionStore.close();
              }
              isRecording && stopRecorder({ addIssueId: res?.id });
              resolve(res?.id);
            } else {
              reject();
            }
            return;
          }
          if (
            fieldStore.fieldValues.value.summary ||
            fieldStore.fieldValues.value.summary ||
            fieldStore.fieldValues.value.attachment?.length ||
            fieldStore.fieldValues.value.relationship?.length ||
            fieldStore.fieldValues.value.subtask?.length ||
            fieldStore.fieldValues.value.docs?.length ||
            isRecording ||
            hasChanged(
              fieldStore.fieldValues.value,
              (actionStore.action.value as IssueDrawerActionCreate).defaultData
            )
          ) {
            const message = instance.$t(
              isRecording
                ? 'issue.dialog.recordCloseConfirm.createMsg'
                : 'issue.dialog.closeConfirm.createMsg'
            );
            const title = instance.$t('issue.dialog.closeConfirm.title');
            instance.$msgbox({
              title,
              message,
              confirmButtonText: instance.$t(
                'issue.dialog.closeConfirm.completeCreation'
              ),
              cancelButtonText: instance.$t(
                'issue.dialog.closeConfirm.cancelCreate'
              ),
              showCancelButton: true,
              closeOnPressEscape: false,
              distinguishCancelAndClose: true,
              customClass: 'zk-auto-test_issue-detail-save-prompt',
              callback: async (action: 'close' | 'confirm' | 'cancel') => {
                if (action === 'cancel') {
                  actionStore.close();
                  stopRecorder({ isCancel: true });
                  resolve(undefined);
                } else if (action === 'confirm') {
                  const res = await fieldStore.submitCreateIssue();
                  if (res) {
                    actionStore.close();
                    resolve(res.id);
                    stopRecorder({ addIssueId: res.id });
                  } else {
                    stopRecorder();
                    reject();
                  }
                } else {
                  reject();
                }
              }
            });
            instance.$track.send(
              'Issue_RemindLeave',
              {
                Issue_Status: '创建',
                Has_Record_Video: isRecording ? '是' : '否'
              },
              true
            );
          } else {
            actionStore.close();
            resolve(undefined);
          }
        } else if (actionStore.action.value.type === 'Edit') {
          // 如果没有权限则直接关闭（协作者）
          if (disabled.value) {
            actionStore.close();
            resolve(undefined);
            return;
          }
          if (isRecording) {
            try {
              await showEditCloseMessageBox();
            } catch (error) {
              return reject();
            }
          }
          waitChangedFlushOnEdit()
            .then(() => {
              actionStore.close();
              resolve(undefined);
            })
            .catch(reject);
        } else if (actionStore.action.value.type === 'PreviewType') {
          actionStore.close();
          resolve(undefined);
        }
      } else {
        actionStore.close();
        resolve(undefined);
      }
    });
  };

  const projectChangedEvent = createEventHook<number>();

  const requestChangeProject = (id: number) => {
    if (
      projectStore.projectList.value.some((p) => p.id === id) &&
      id !== projectStore.currentProjectId.value &&
      actionStore.action.value?.type === 'Create'
    ) {
      const relationship = actionStore.action.value.copyFromIssueId
        ? (fieldStore.fieldValues.value.relationship || []).filter(
          (relation: IssueRelationship) =>
            relation.type === 'IS_DUPLICATES_BY' &&
              relation.issueId ===
                (actionStore.action.value as IssueDrawerActionCreate)
                  .copyFromIssueId
        )
        : undefined;
      actionStore.createIssue(id, undefined, undefined, {
        summary: fieldStore.fieldValues.value.summary,
        description: fieldStore.fieldValues.value.description,
        relationship
      });
      projectChangedEvent.trigger(id);
    }
  };

  let pinLoading = false;

  const { t } = useI18n();

  const togglePin = async () => {
    if (pinLoading) {
      return;
    }
    if (issueStore.issueAssemble.value) {
      pinLoading = true;
      if (actionStore.actionType.value === 'Edit') {
        if (issueStore.isPin.value) {
          try {
            issueStore.issueAssemble.value.pinStatus = 0;
            await vuex.dispatch('pin/cancelPin', {
              id: issueStore.issueAssemble.value.issueVO.id
            });
            instance.$message.success(t('Pin.PinCanceled'));
          } finally {
            pinLoading = false;
          }
        } else {
          try {
            await safeClose();
            const id = issueStore.issueAssemble.value.issueVO.id;
            await vuex.dispatch('pin/addPin', {
              id
            });
            issueStore.issueAssemble.value.pinStatus = 1;
          } finally {
            pinLoading = false;
          }
        }
      } else if (actionStore.actionType.value === 'Create') {
        try {
          const id = await safeClose(true);
          if (id) {
            await vuex.dispatch('pin/addPin', {
              id
            });
          }
        } finally {
          pinLoading = false;
        }
      }
    }
  };


  watch(actionStore.action, () => {
    leftNavigationLabelStore.reset();
  });

  return {
    isAdd: computed(() => actionStore.actionType.value === 'Create'),
    isEdit: computed(() => actionStore.actionType.value === 'Edit'),
    isTypePreview: computed(
      () => actionStore.actionType.value === 'PreviewType'
    ),
    id: computed(() =>
      actionStore.action.value?.type === 'Edit'
        ? actionStore.action.value.issueId
        : null
    ),
    safeClose,
    togglePin,
    ...actionStore,
    show: readonly(show),
    opened,
    disabled,
    stateFieldEditable,
    localDb,
    loading,
    ...shareStore,
    ...issueStore,
    ...projectStore,
    ...issueTypeStore,
    ...sprintStore,
    ...layoutStore,
    ...workflowStore,
    ...fieldStore,
    // ...docsStore,
    ...recorderStore,
    ...useRelationNavigation(actionStore, fieldStore, waitChangedFlushOnEdit, layoutStore),
    ...useTurnPage(
      actionStore,
      projectChangedEvent.on,
      sprintStore.onSprintChanged
    ),
    ...useThirdTools(actionStore, issueStore.issueAssemble),
    ...recordStore,
    requestChangeProject,
    waitChangedFlushOnEdit,
    triggerIssueSubmitEvent,
    ...useComment(
      actionStore,
      issueStore,
      fieldStore,
      layoutStore,
      shareStore.isVisitor
    ),
    ...useFormula(actionStore, fieldStore, shareStore.isVisitor),
    onIssueLoadComplete: issueLoadCompleteEvent.on,
    triggerIssueLoadComplete: issueLoadCompleteEvent.trigger,
    ...useReport(shareStore.isVisitor),
    ...useUIState(actionStore),
    suuid: computed(() => shareStore.isVisitor.value ? vuex.state.user.shareInfo.suuid : vuex.state.user.org.suuid),
    ...permissionStore,
    ...leftNavigationLabelStore
  };
});

export type IssueStore = ReturnType<typeof useIssueDetail>;
